<template>
  <div class="col-12" v-if="account">
    <div class="card">
      <div class="card-body">
        <h4 class="card-title w-100">
          <div class="d-inline-block">
            {{ account.alias }} <br />
            <small class="text-black-50">{{ account.address }}</small>
          </div>
        </h4>
        <p class="card-text"></p>
        <hr />
        <h4>Riepilogo</h4>
        <div v-if="stats == null && statsLoaded">
          <p><i>Non sono disponibili statistiche per questo account</i></p>
        </div>
        <div v-if="stats != null" class="charts" :data-account="account.uuid">
          <div class="chart-container" style="position: relative">
            <canvas data-category="summary"></canvas>
          </div>
          <div v-for="(sections, category) in stats" :key="category">
            <h4>{{ category }}</h4>
            <div class="row" :data-category="category">
              <div
                class="col-sm-4"
                v-for="(years, section) in sections"
                :key="section"
              >
                <div class="chart-container" style="position: relative">
                  <canvas :data-section="section"></canvas>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import Chart from 'chart.js/auto';
import * as accountService from '@/services/account.service.js';
import { ref } from 'vue';
import { onMounted } from "vue";
import { nextTick } from "vue";
import { useIsMounted } from '@/utils';

const props = defineProps(['accountUuid']);
const isMounted = useIsMounted();

const account = ref(null);
const stats = ref(null);
const statsLoaded = ref(false);
const colors = [
  [221, 75, 57], // red
  [0, 166, 90], // green
  [243, 156, 18], // yellow
  [0, 115, 183], // blue
  [231, 22, 128], // archivium logo
];

/**
 * Funzione che crea i chart per tutte le varie sezioni
 */
function initCharts() {
  if (!isMounted.value) return;

  summaryChart();
  for (let category in stats.value) {
    for (let section in stats.value[category]) {
      sectionChart(category, section);
    }
  }
}

/**
 * Genera la stringa rgb(r,g,b) per il colore
 * @param {number[]} rgb Il colore in [r, g, b]
 * @returns {string}
 */
function translateToColor(rgb) {
  return "rgb(" + rgb[0] + ", " + rgb[1] + ", " + rgb[2] + ")";
}

/**
 * Genera un colore random evitando di ricreare un colore già esistente
 * @param {number[][]} colors
 * @returns {number[]}
 */
function createRandomColor(colors) {
  var r = Math.floor(Math.random() * 256);
  var g = Math.floor(Math.random() * 256);
  var b = Math.floor(Math.random() * 256);

  for (let i in colors) {
    if (colors[i][0] === r && colors[i][1] === g && colors[i][2] === b) {
      return createRandomColor(colors);
    }
  }

  return [r, g, b];
}

/**
 * Ottiene un'array di lunghezza arbitraria con dei colori da poter
 * utilizzare nei chartflow (i primi 5 sono inseriti a mano, gli altri
 * sono generati casualmente)
 * @param {number}  length
 * @returns {string[]}
 */
function getNRandomColorsStrings(length) {
  if (length <= colors.length) {
    return colors.slice(0, length).map(c => translateToColor(c));
  }

  let randomColors = new Array(length - colors.length)
    .fill(null) // necessario per il .map
    .map((_) => createRandomColor(colors));

  return [...colors, ...randomColors].map(c => translateToColor(c));
}

/**
 * Ripulisci una stringa per poterla usare come etichetta nei
 * chartflow (sostituisce i '-' e capitalizza).
 * @param {string} label
 * @returns {string}
 */
function normalizeLabel(label) {
  label = label.replaceAll('-', ' ');
  return label[0].toUpperCase() + label.substring(1);
}

/**
 * Popola il diagramma a barre con il riepilogo delle email lette/non lette
 * divise per anni
 */
function summaryChart() {
  var colorStrings = getNRandomColorsStrings(2);

  var ctxRiepilogo = document.querySelector(
    ".charts[data-account='" +
    account.value.uuid +
    "'] canvas[data-category='summary']"
  );

  const datiRiepilogo = stats.value.ricevute.lettura;

  // I dati hanno questa forma:
  // { "2025" => { "non-lette": 10, "lette": 32 } }
  let years = Object.keys(datiRiepilogo);
  let labels = Object.keys(Object.values(datiRiepilogo)[0])
    .map(label => normalizeLabel(label));

  // Rimuovo tutte le etichette, avendo una matrice bidimensionale pulita
  let datiPuliti = Object.values(datiRiepilogo).map((year) => Object.values(year));
  // Trasposizione della matrice
  datiPuliti = datiPuliti[0].map((_, colIndex) => datiPuliti.map(row => row[colIndex]));

  let datasets = datiPuliti.map((data, index) => ({
    label: labels[index],
    data,
    backgroundColor: colorStrings[index],
    borderColor: colorStrings[index],
    borderWidth: 1,
    barThickness: 10
  }));

  // calcola l'aspect-ratio del diagramma
  // h: numero di anni da visualizzare * 100 + spazio per la legenda
  let h = years.length * 100 + 50;
  // w: larghezza del contenitore
  let w = ctxRiepilogo.parentElement.getBoundingClientRect().width;

  // creo il chart di riepilogo
  new Chart(ctxRiepilogo, {
    type: "bar",
    data: {
      labels: years,
      datasets: datasets,
    },
    options: {
      responsive: true,
      aspectRatio: (w/h),
      indexAxis: "y",
    },
  });
}

/**
 * Crea un chartflow a ciambella per una sezione delle statistiche
 * @param {string} category
 * @param {string} section
 */
function sectionChart(category, section) {
  const data = stats.value[category][section];

  var element = document.querySelector(
    `.charts[data-account="${account.value.uuid}"] ` +
    `[data-category="${category}"] ` +
    `[data-section="${section}"]`
  );

  // Somma i dati di ogni anno in una sola mappa
  let summedData = {};

  for (let year in data) {
    for (let label in data[year]) {
      let value = data[year][label];
      summedData[label] = value + (summedData[label] ?? 0);
    }
  }

  let labels = Object.keys(summedData).map(label => normalizeLabel(label));
  let chartData = Object.values(summedData);
  let colorStrings = getNRandomColorsStrings(chartData.length);

  new Chart(element, {
    type: "doughnut",
    data: {
      labels,
      datasets: [
        {
          data: chartData,
          backgroundColor: colorStrings,
          borderColor: colorStrings,
          borderWidth: 0,
        },
      ],
    },
    options: {
      cutout: '50%',
      radius: 80,
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "left",
          labels: {
            boxWidth: 10,
            font: {
              size: 10
            },
          }
        }
      }
    },
  });
}

// Init
onMounted(async () => {
  let res = await accountService.getStats(props.accountUuid);
  account.value = res.account;
  statsLoaded.value = true;

  // check if stats are empty
  if (!res.stats.ricevute.lettura) {
    return;
  }

  stats.value = res.stats;

  setTimeout(() => initCharts(), 500);
});
</script>
